import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Global from '../../../../../store/global-selectors';
import { refreshStoreList } from '../../../../../store/seller/seller.actions';
import { getReferenceData } from '../../../../../store/reference-data/reference-data.actions';
import { LocalStorageService } from '../../../../../services/local-storage-service';
import { CONNECTED_MARKETPLACES_GUIDS } from '../../../../settings/stores/marketplace-connection-modal/store-connection-success-notifications';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { EventSourceStrings, Workflows } from '../../../../../tracking/tracking-constants';
import { MarketplaceIds } from '../../../../../enums/marketplace-ids';

const MarketplaceContext = createContext();

export const MarketplaceProvider = ({ children }) => {
  const dispatch = useDispatch();
  const marketplacesReference = useSelector(Global.selectReferenceDatagetMarketplaces);
  const allSellerStores = useSelector(Global.selectSellerStores);
  const activeSellerStores = useMemo(() => {
    return allSellerStores.filter((store) => store?.isActive && store?.marketplaceId !== MarketplaceIds.ShipStation);
  }, [allSellerStores]);
  const activeSellerStoreGuidList = useMemo(() => {
    return activeSellerStores.map((store) => store?.storeGuid);
  }, [activeSellerStores]);
  const [setupMarketplace, setSetupMarketplace] = useState(undefined);
  const trackMarketplaceConnection = useTrackWorkflow(Workflows.MARKETPLACE_CONNECTION_FLOW);

  const onMarketplaceSelected = useCallback(
    (marketplace) => {
      LocalStorageService.setItem(CONNECTED_MARKETPLACES_GUIDS, activeSellerStoreGuidList);
      trackMarketplaceConnection(WorkflowSteps.START, {
        source: EventSourceStrings.FTU_WIZARD_ORDERS_PAGE,
      });
      setSetupMarketplace(marketplace);
      trackMarketplaceConnection(WorkflowSteps.CLICK, {
        selectedMarketplace: marketplace,
        marketplaceName: marketplace?.name,
        marketplaceId: marketplace?.marketplaceId,
      });
    },
    [activeSellerStoreGuidList, trackMarketplaceConnection],
  );

  const closeSetupModal = useCallback(() => {
    if (setupMarketplace) {
      const setupMarketplaceWithoutLogo = {
        ...setupMarketplace,
        logo: undefined,
      };
      trackMarketplaceConnection(WorkflowSteps.CANCEL, {
        selectedMarketplace: setupMarketplaceWithoutLogo,
        marketplaceName: setupMarketplaceWithoutLogo?.name,
        marketplaceId: setupMarketplaceWithoutLogo?.marketplaceId,
      });
    }
    setSetupMarketplace(undefined);
  }, [setupMarketplace, trackMarketplaceConnection]);

  const onMarketplaceSetupBack = useCallback(() => {
    setSetupMarketplace(undefined);
  }, []);

  const onSaveNewMarketplace = useCallback(() => {
    const { marketplaceId, name } = setupMarketplace || {};
    if (!marketplacesReference.some((market) => market.marketplaceId === marketplaceId)) {
      dispatch(getReferenceData());
    }
    dispatch(refreshStoreList());

    const setupMarketplaceWithoutLogo = {
      ...setupMarketplace,
      logo: undefined,
    };
    trackMarketplaceConnection(WorkflowSteps.COMPLETE, {
      selectedMarketplace: setupMarketplaceWithoutLogo,
      marketplaceName: name,
      marketplaceId: setupMarketplaceWithoutLogo?.marketplaceId,
    });

    LocalStorageService.clearItemsWithPrefix(CONNECTED_MARKETPLACES_GUIDS);
    setSetupMarketplace(undefined);
  }, [dispatch, marketplacesReference, setupMarketplace, trackMarketplaceConnection]);

  const onError = useCallback(
    (error) => {
      const errorMessage = error?.error?.ExceptionMessage || error?.message;
      trackMarketplaceConnection(WorkflowSteps.ERROR, {
        error,
        errorMessage,
      });
    },
    [trackMarketplaceConnection],
  );

  const onRedirectToMarketplaceSetup = useCallback(() => {
    if (setupMarketplace) {
      const setupMarketplaceWithoutLogo = {
        ...setupMarketplace,
        logo: undefined,
      };
      trackMarketplaceConnection(WorkflowSteps.REDIRECT, {
        selectedMarketplace: setupMarketplaceWithoutLogo,
        marketplaceName: setupMarketplaceWithoutLogo?.name,
        marketplaceId: setupMarketplaceWithoutLogo?.marketplaceId,
      });
    }
  }, [setupMarketplace, trackMarketplaceConnection]);

  const startMarketplaceConnection = useCallback(() => {
    LocalStorageService.setItem(CONNECTED_MARKETPLACES_GUIDS, activeSellerStoreGuidList);
    trackMarketplaceConnection(WorkflowSteps.START, {
      source: EventSourceStrings.FTU_WIZARD_ORDERS_PAGE,
    });
  }, [activeSellerStoreGuidList, trackMarketplaceConnection]);

  const cancelMarketplaceConnection = useCallback(
    (source) => {
      trackMarketplaceConnection(WorkflowSteps.CANCEL, { source });
    },
    [trackMarketplaceConnection],
  );

  const value = {
    setupMarketplace,
    onMarketplaceSelected,
    closeSetupModal,
    onMarketplaceSetupBack,
    onSaveNewMarketplace,
    onError,
    onRedirectToMarketplaceSetup,
    startMarketplaceConnection,
    cancelMarketplaceConnection,
  };

  return <MarketplaceContext.Provider value={value}>{children}</MarketplaceContext.Provider>;
};

export const useMarketplaceContext = () => {
  const context = useContext(MarketplaceContext);
  if (context === undefined) {
    throw new Error('useMarketplaceContext must be used within a MarketplaceProvider');
  }
  return context;
};
